import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { CalendarRange, ChevronLeft, ChevronRight, CloudSun, Plus, Tractor, User, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ChantierDialog } from '../components/planning/ChantierDialog';
import { NouveauChantierDialog } from '../components/planning/NouveauChantierDialog';

const API = process.env.REACT_APP_BACKEND_URL;
export const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
const COULEURS = ['bg-emerald-600', 'bg-sky-600', 'bg-amber-600', 'bg-rose-600', 'bg-violet-600', 'bg-teal-600', 'bg-orange-600'];
const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
const lundi = (d) => { const x = new Date(d); x.setHours(12); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return iso(x); };
const decaler = (s, n) => { const x = new Date(`${s}T12:00:00`); x.setDate(x.getDate() + n); return iso(x); };
const AUJ = iso(new Date());
const SANS = { type: 'aucune', id: '__sans__', nom: 'Sans machine ni personne' };
const joursDe = (c) => (c.date_debut ? Array.from({ length: Math.max(1, c.nb_jours || 1) }, (_, i) => decaler(c.date_debut, i)) : []);

const EnAttente = ({ items, meteo, onOuvrir }) => (
  <div data-testid="planning-en-attente" className="bg-white border border-stone-200 rounded-2xl p-4">
    <p className="font-semibold text-stone-800 flex items-center gap-2"><CloudSun className="w-5 h-5 text-amber-500" />En attente (météo ou date)</p>
    {meteo && <p data-testid="planning-meteo" className="text-xs text-stone-500 mt-1">Pluie des 4 derniers jours : {meteo.pluie_4_derniers_jours} mm · premier jour favorable : {meteo.premier_jour_favorable || 'pas avant 5 jours'}</p>}
    <div className="space-y-2 mt-3">
      {items.map((c) => (
        <button key={c.id} data-testid={`attente-${c.id}`} onClick={() => onOuvrir(c)} className="w-full text-left p-3 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors">
          <p className="text-sm font-medium text-stone-800">{c.titre}</p>
          <p className="text-xs text-stone-500">{c.condition_meteo || 'Date à fixer'}</p>
        </button>
      ))}
      {items.length === 0 && <p className="text-xs text-stone-400">Rien en attente.</p>}
    </div>
  </div>
);

export default function PlanningRessourcesPage() {
  const [debut, setDebut] = useState(lundi(new Date()));
  const [data, setData] = useState(null);
  const [res, setRes] = useState(null);
  const [meteo, setMeteo] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const [creation, setCreation] = useState(false);
  const [filtre, setFiltre] = useState('');

  const charger = useCallback(async () => {
    const r = await fetch(`${API}/api/planning-ressources/semaine?debut=${debut}`, { headers: hdr() });
    if (r.ok) setData(await r.json());
  }, [debut]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    fetch(`${API}/api/planning-ressources/ressources`, { headers: hdr() }).then((r) => r.ok && r.json()).then(setRes);
    fetch(`${API}/api/planning-ressources/meteo`, { headers: hdr() }).then((r) => r.ok && r.json()).then(setMeteo).catch(() => {});
  }, []);

  const lignes = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    [...data.chantiers, ...data.en_attente].forEach((c) => c.ressources.forEach((r) => m.set(r.id, r)));
    if (data.chantiers.some((c) => c.ressources.length === 0)) m.set(SANS.id, SANS);
    return [...m.values()].filter((r) => r.nom.toLowerCase().includes(filtre.toLowerCase()))
      .sort((a, b) => (a.type === b.type ? a.nom.localeCompare(b.nom) : a.type === 'materiel' ? -1 : 1));
  }, [data, filtre]);
  const couleur = useMemo(() => Object.fromEntries((data?.chantiers || []).map((c, i) => [c.id, COULEURS[i % COULEURS.length]])), [data]);
  const conflit = (rid, j) => data?.conflits.some((x) => x.ressource_id === rid && x.jour === j);

  return (
    <div data-testid="planning-ressources-page" className="p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 flex items-center gap-3"><CalendarRange className="w-8 h-8 text-emerald-700" />Planning chantiers</h1>
          <p className="text-sm text-stone-500 mt-1">Par machine et par personne. Chaque chantier est relié aux parcelles, aux stocks, aux heures et à la facture.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="planning-sem-prec" variant="outline" size="icon" onClick={() => setDebut(decaler(debut, -7))}><ChevronLeft className="w-4 h-4" /></Button>
          <span data-testid="planning-semaine" className="text-sm font-medium w-40 text-center">Semaine du {new Date(debut).toLocaleDateString('fr-FR')}</span>
          <Button data-testid="planning-sem-suiv" variant="outline" size="icon" onClick={() => setDebut(decaler(debut, 7))}><ChevronRight className="w-4 h-4" /></Button>
          <Button data-testid="planning-nouveau-btn" className="rounded-full bg-emerald-700 hover:bg-emerald-800 ml-2" onClick={() => setCreation(true)}><Plus className="w-4 h-4 mr-1" />Chantier</Button>
        </div>
      </div>
      {!data ? <Loader2 className="w-6 h-6 animate-spin text-stone-400" /> : (
        <div className="grid 2xl:grid-cols-[1fr_300px] xl:grid-cols-[1fr_260px] gap-6 items-start">
          <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto">
            <div className="p-3 border-b border-stone-100"><Input data-testid="planning-filtre" placeholder="Filtrer une machine ou une personne…" value={filtre} onChange={(e) => setFiltre(e.target.value)} className="max-w-xs" /></div>
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="bg-stone-50">
                <th className="text-left p-2 w-56 font-medium text-stone-600">Ressource</th>
                {data.jours.map((j) => <th key={j} className={`p-2 font-medium capitalize ${j === AUJ ? 'text-emerald-700' : 'text-stone-600'}`}>{new Date(`${j}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}</th>)}
              </tr></thead>
              <tbody>
                {lignes.map((r) => (
                  <tr key={r.id} className="border-t border-stone-100" data-testid={`ligne-${r.id}`}>
                    <td className="p-2 text-stone-700"><span className="flex items-center gap-1.5">{r.type === 'materiel' ? <Tractor className="w-3.5 h-3.5 text-stone-400" /> : <User className="w-3.5 h-3.5 text-stone-400" />}{r.nom}</span></td>
                    {data.jours.map((j) => {
                      const cs = data.chantiers.filter((c) => joursDe(c).includes(j) && (r.id === SANS.id ? c.ressources.length === 0 : c.ressources.some((x) => x.id === r.id)));
                      return (
                        <td key={j} className={`p-1 align-top ${conflit(r.id, j) ? 'bg-rose-50' : j === AUJ ? 'bg-emerald-50/60' : ''}`}>
                          {cs.map((c) => (
                            <button key={c.id} data-testid={`bloc-${c.id}-${r.id}-${j}`} onClick={() => setOuvert(c)}
                              className={`${couleur[c.id]} ${c.statut === 'termine' ? 'opacity-50 line-through' : ''} text-white text-xs rounded-md px-1.5 py-1 w-full text-left mb-1 truncate hover:opacity-90`}>{c.statut === 'termine' ? '✓ ' : ''}{c.titre}</button>
                          ))}
                          {conflit(r.id, j) && <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {lignes.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-stone-400">Aucun chantier cette semaine.</td></tr>}
              </tbody>
            </table>
          </div>
          <EnAttente items={data.en_attente} meteo={meteo} onOuvrir={setOuvert} />
        </div>
      )}
      {ouvert && <ChantierDialog chantier={ouvert} onClose={() => setOuvert(null)} onMaj={(c) => { setOuvert(c); charger(); toast.success('Chantier mis à jour'); }} />}
      {creation && res && <NouveauChantierDialog ressources={res} onClose={() => setCreation(false)} onCree={() => { setCreation(false); charger(); toast.success('Chantier ajouté'); }} />}
    </div>
  );
}
